import KycService from "../../services/kyc-service";
import { KYC_DEFAULT_FILTER_PAYLOAD } from "./kycDesignShared";
import { formatKycCreatedOnDate, getKycAppliedFilterPayload } from "./kycHelpers";

const DATE_FIELD_KEYS = new Set([
	"complaintDate",
	"invoiceDate",
	"dispatchDate",
	"createdOn",
]);

const normalizeFieldKey = (key) =>
	String(key || "")
		.replace(/^ispl_/i, "")
		.replace(/[_-]/g, "")
		.toLowerCase();

export const getQualityIssueFieldValue = (record, keys = []) => {
	if (!record || typeof record !== "object") {
		return "";
	}

	for (const key of keys) {
		const value = record[key];
		if (value !== undefined && value !== null && String(value).trim() !== "") {
			return value;
		}
	}

	const normalizedKeys = keys.map(normalizeFieldKey);
	for (const [key, value] of Object.entries(record)) {
		if (value === undefined || value === null || String(value).trim() === "") {
			continue;
		}
		if (normalizedKeys.includes(normalizeFieldKey(key))) {
			return value;
		}
	}

	return "";
};

const formatQualityIssueValue = (key, value) => {
	if (value === undefined || value === null || String(value).trim() === "") {
		return "-";
	}
	if (DATE_FIELD_KEYS.has(key)) {
		const formatted = formatKycCreatedOnDate(value);
		return formatted !== "-" ? formatted : String(value).trim();
	}
	return String(value).trim();
};

export const QUALITY_ISSUE_TABLE_FIELDS = [
	{ label: "Customer", key: "customer", keys: ["customer", "customerName", "ispl_customer", "accountName"] },
	{ label: "Email", key: "email", keys: ["email", "emailAddress", "customerEmail"] },
	{ label: "Mobile", key: "mobile", keys: ["mobile", "mobilePhone", "phone", "contactNo"] },
	{ label: "Contact", key: "contact", keys: ["contact", "contactName", "primaryContact"] },
	{ label: "Invoice Date", key: "invoiceDate", keys: ["invoiceDate", "invoice_Date"] },
	{ label: "Invoice No", key: "invoiceNo", keys: ["invoiceNo", "invoiceNumber", "invoice_No"] },
	{ label: "Dispatch Date", key: "dispatchDate", keys: ["dispatchDate", "dispatch_Date"] },
	{ label: "Dispatch Qty", key: "dispatchQty", keys: ["dispatchQty", "dispatchQuantity", "dispatch_Qty"] },
	{ label: "Order", key: "order", keys: ["order", "orderNo", "orderNumber", "soNumber"] },
	{ label: "Order Request Line", key: "orderRequestLine", keys: ["orderRequestLine", "orderLine", "lineNo"] },
	{ label: "Affected Quantity", key: "affectedQuantity", keys: ["affectedQuantity", "quantity", "affectedQty"] },
	{ label: "Complaint No", key: "complaintNo", keys: ["complaintNo", "complaintNumber", "complaint_No", "ticketNo", "ticketNumber"] },
	{ label: "Topic", key: "topic", keys: ["topic", "complaintTopic", "subject"] },
	{ label: "Complaint Date", key: "complaintDate", keys: ["complaintDate", "complaint_Date", "createdOn"] },
	{ label: "Mode of Complaint", key: "modeOfComplaint", keys: ["modeOfComplaint", "complaintMode", "mode"] },
	{ label: "Complaint Type", key: "complaintType", keys: ["complaintType", "type"] },
	{ label: "Complaint Sub Type", key: "complaintSubType", keys: ["complaintSubType", "subType", "complaintSub_Type"] },
	{ label: "Priority", key: "priority", keys: ["priority", "complaintPriority"] },
	{ label: "Complaint Description", key: "complaintDescription", keys: ["complaintDescription", "description", "remarks"] },
	{ label: "Assignee Suggestion", key: "assigneeSuggestion", keys: ["assigneeSuggestion", "suggestion"] },
	{ label: "Feedback of Assignee", key: "feedbackOfAssignee", keys: ["feedbackOfAssignee", "assigneeFeedback", "feedback"] },
	{ label: "Preventive Action", key: "preventiveAction", keys: ["preventiveAction", "preventive_Action"] },
	{ label: "Credit Memo", key: "creditMemo", keys: ["creditMemo", "credit_Memo"] },
	{ label: "Generate Credit Note", key: "generateCreditNote", keys: ["generateCreditNote", "generate_CreditNote"] },
	{ label: "Open Days", key: "openDays", keys: ["openDays", "open_Days"] },
	{ label: "Complaint Status", key: "complaintStatus", keys: ["complaintStatus", "status"] },
	{ label: "Material", key: "material", keys: ["material", "materialName", "ispl_product"] },
	{ label: "Owner", key: "ownerid", keys: ["ownerid", "ownerId", "owner"] },
];

export const getQualityIssueDisplayValue = (record, field) =>
	formatQualityIssueValue(field.key, getQualityIssueFieldValue(record, field.keys));

export const parseQualityIssueMasterListResponse = (response) => {
	const success = response?.data?.statusCode === 200 || response?.data?.status === true;
	if (!success) {
		return [];
	}
	const responseData = response.data.responseData || {};
	const qualityIssueList = Array.isArray(responseData)
		? responseData
		: responseData.qualityIssueMasterLists ||
			responseData.qualityIssueMasterList ||
			responseData.kycQualityIssueMasterLists ||
			responseData.qualityIssues ||
			responseData.data ||
			[];
	return Array.isArray(qualityIssueList) ? qualityIssueList : [];
};

export const parseQualityIssueMasterTableResponse = (response, fallbackPage = 1) => {
	const rows = parseQualityIssueMasterListResponse(response);
	const responseData = response?.data?.responseData || {};
	const totalRecords = Number(
		Array.isArray(responseData) ? rows.length : responseData.totalRecords || rows.length || 0
	);
	const rawPage = Array.isArray(responseData) ? undefined : responseData.currentPage;
	const parsedPage = Number(rawPage);
	const currentPage =
		rawPage !== undefined &&
		rawPage !== null &&
		rawPage !== "" &&
		!Number.isNaN(parsedPage) &&
		parsedPage > 0
			? parsedPage
			: fallbackPage;
	return { rows, totalRecords, currentPage };
};

const QUALITY_ISSUE_DEFAULT_PAYLOAD = {
	customer: "",
	complaintType: "",
	complaintSubType: "",
	priority: "",
	modeOfComplaint: "",
	material: "",
	complaintStatus: "",
	materialName: "",
};

export const fetchQualityIssueMasterList = async ({
	ispl_product = "",
	ispl_customer = "",
	customer = "",
	complaintType = "",
	complaintSubType = "",
	priority = "",
	modeOfComplaint = "",
	material = "",
	complaintStatus = "",
	materialName = "",
	search = "",
	pageindex = 1,
	pagesize = 10,
	sortcolumn = "complaintDate",
	orderBy = "DESC",
	groupCompanyId = "",
} = {}) => {
	const trimmedProduct = String(ispl_product || material || materialName || "").trim();
	const trimmedCustomer = String(ispl_customer || customer || "").trim();
	const requestData = {
		pageindex,
		pagesize,
		sortcolumn: sortcolumn || "",
		orderBy: orderBy || "",
		search: String(search || "").trim(),
		...KYC_DEFAULT_FILTER_PAYLOAD,
		...QUALITY_ISSUE_DEFAULT_PAYLOAD,
		...getKycAppliedFilterPayload(),
		customer: trimmedCustomer,
		complaintType: String(complaintType || "").trim(),
		complaintSubType: String(complaintSubType || "").trim(),
		priority: String(priority || "").trim(),
		modeOfComplaint: String(modeOfComplaint || "").trim(),
		material: trimmedProduct,
		complaintStatus: String(complaintStatus || "").trim(),
		materialName: String(materialName || trimmedProduct).trim(),
		ispl_customer: trimmedCustomer,
		ispl_product: trimmedProduct,
	};

	const trimmedGroupCompanyId = String(groupCompanyId || "").trim();
	if (trimmedGroupCompanyId) {
		requestData.groupcomanyId = trimmedGroupCompanyId;
	}

	const response = await KycService.getKycQualityIssueMasterListAPI(requestData);
	return parseQualityIssueMasterTableResponse(response, pageindex);
};
